import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api, { getApiError } from '../lib/axios';
import { Loader2, AlertCircle } from 'lucide-react';

export default function OAuthCallback() {
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code || !provider) {
      setError('Invalid OAuth callback');
      return;
    }

    const handleCallback = async () => {
      try {
        const tokenResponse = await api.post(
          `/api/auth/oauth/${provider}/callback`,
          { code }
        );
        const { access_token, refresh_token } = tokenResponse.data;

        const userResponse = await api.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${access_token}` },
        });

        setAuth(userResponse.data, { access_token, refresh_token });
        navigate('/dashboard', { replace: true });
      } catch (err: any) {
        setError(
          getApiError(err, `Failed to sign in with ${provider}. Please try again.`)
        );
      }
    };

    handleCallback();
  }, [provider, searchParams, navigate, setAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Authentication Failed
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-brand-600 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">
          Signing in with {provider}...
        </p>
      </div>
    </div>
  );
}
